import { zodResolver } from "@hookform/resolvers/zod";
import { endOfDay, format, parse, startOfDay } from "date-fns";
import { useCallback, useContext, useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { MultiValue } from "react-select";
import { z } from "zod";
import { DateRange } from "react-day-picker";
import { TransactionContext } from "../../../../../app/contexts/TransactionContext";
import { OptionType } from "../../../../../app/utils/optionSelectType";
import { useSellers } from "../../../../../app/hooks/useSellers";
import { useCustomers } from "../../../../../app/hooks/useCustomers";
import { useTeams } from "../../../../../app/hooks/useTeams";
import { useAuth } from "../../../../../app/hooks/useAuth";
import { PartnerType } from "@/app/utils/partners.enum";
import { capitalizeFirstLetter } from "@/app/utils/capitalizeFirstLetter";

const schema = z.object({
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  sellerIds: z.array(z.string()).optional(),
  teamIds: z.array(z.string()).optional(),
  customerIds: z.array(z.string()).optional(),
  status: z.array(z.string()).optional(),
  product: z.array(z.string()).optional(),
});

type FormFilterData = z.infer<typeof schema>;

export const metrics = [
  { value: "count" as const },
  { value: "total" as const },
] as const;

const metricsLength = metrics.length;

interface UseTransactionFilterControllerProps {
  metric?: "count" | "total";
  setMetric?: (metric: "count" | "total") => void;
}

export const useTransactionFilterController = ({
  metric: metricProp,
  setMetric,
}: UseTransactionFilterControllerProps = {}) => {
  const [selectedSellerOptions, setSelectedSellerOptions] = useState<
    MultiValue<OptionType>
  >([]);
  const [selectedCustomerOptions, setSelectedCustomerOptions] = useState<
    MultiValue<OptionType>
  >([]);
  const [selectedTeamOptions, setSelectedTeamOptions] = useState<
    MultiValue<OptionType>
  >([]);
  const [selectedStatusOptions, setSelectedStatusOptions] = useState<
    MultiValue<OptionType>
  >([]);
  const [selectedDate, setSelectedDate] = useState<DateRange | undefined>(
    undefined,
  );
  const [selectedProductOptions, setSelectedProductOptions] = useState<
    MultiValue<OptionType>
  >([]);
  const [hasOpenedFilter, setHasOpenedFilter] = useState(false);

  const handleFilterPopoverOpenChange = useCallback((open: boolean) => {
    setHasOpenedFilter((prevState) => prevState || open);
  }, []);

  const getInitialSelected = () => {
    if (metricProp) {
      const index = metrics.findIndex((m) => m.value === metricProp);
      return index >= 0 ? index : 0;
    }
    return 0;
  };

  const [selected, setSelected] = useState(() => getInitialSelected());

  useEffect(() => {
    if (metricProp) {
      const index = metrics.findIndex((m) => m.value === metricProp);
      if (index >= 0) {
        setSelected(index);
      }
    }
  }, [metricProp]);

  const changeMetric = () => {
    const nextIndex = (selected + 1) % metricsLength;
    setSelected(nextIndex);
    if (setMetric) {
      setMetric(metrics[nextIndex].value);
    }
  };

  const productOptions = Object.values(PartnerType).map((product) => ({
    value: product,
    label: capitalizeFirstLetter(product),
  }));

  const { setFilterParams } = useContext(TransactionContext);

  const { isSeller, user } = useAuth();
  const shouldLoadFilterData = hasOpenedFilter;

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<FormFilterData>({
    resolver: zodResolver(schema),
  });

  const {
    data: fullSellerData,
    isFetching,
    isLoading,
    fetchNextPage,
    hasNextPage,
    setParams: setParamsSeller,
  } = useSellers({ initialLimit: 10, enabled: shouldLoadFilterData });

  const sellerData = fullSellerData?.pages.flatMap(
    (page) =>
      page.users?.map((user) => ({
        value: user._id,
        label: user.name,
      })) || [],
  );

  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetching) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isFetching]);

  const {
    data: fullCustomerData,
    fetchNextPage: fetchCustomers,
    hasNextPage: hasMoreCustomer,
    isFetching: isFetchingCustomer,
    isLoading: isLoadingCustomer,
    setParams: setParamsCustomer,
  } = useCustomers({ initialLimit: 10, enabled: shouldLoadFilterData });

  const customerData = fullCustomerData?.pages.flatMap((page) =>
    page.customers.map((customer) => ({
      value: customer._id,
      label: customer.name,
    })),
  );

  const handleLoadMoreCustomer = useCallback(() => {
    if (hasMoreCustomer && !isFetchingCustomer) {
      fetchCustomers();
    }
  }, [hasMoreCustomer, isFetchingCustomer, fetchCustomers]);

  const {
    data: fullTeamData,
    fetchNextPage: fetchTeam,
    hasNextPage: hasTeam,
    isFetching: isFetchingTeam,
    isLoading: isLoadingTeam,
    setParams: setParamsTeam,
  } = useTeams({
    enabled: shouldLoadFilterData,
    initialParams: {
      sort: "createdAt",
      order: "1",
    },
  });

  const teamsData = fullTeamData?.pages.flatMap((page) =>
    page.teams.map((team) => ({
      value: team._id,
      label: team.name,
    })),
  );

  const handleLoadMoreTeam = () => {
    if (hasTeam && !isFetchingTeam) {
      fetchTeam();
    }
  };

  const handleSelectProduct = (
    selectedProductOptions: MultiValue<OptionType>,
  ) => {
    setValue(
      "product",
      selectedProductOptions?.map((option) => option.value.toString()) || [],
    );
    setSelectedProductOptions(selectedProductOptions);
  };

  const handleSelectSeller = (
    selectedSellerOptions: MultiValue<OptionType>,
  ) => {
    setValue(
      "sellerIds",
      selectedSellerOptions?.map((option) => option.value.toString()) || [],
    );
    setSelectedSellerOptions(selectedSellerOptions);
  };

  const handleSelectCustomer = (
    selectedCustomerOptions: MultiValue<OptionType>,
  ) => {
    setValue(
      "customerIds",
      selectedCustomerOptions?.map((option) => option.value.toString()) || [],
    );
    setSelectedCustomerOptions(selectedCustomerOptions);
  };

  const handleSelectTeam = (selectedTeamOptions: MultiValue<OptionType>) => {
    setValue(
      "teamIds",
      selectedTeamOptions?.map((option) => option.value.toString()) || [],
    );
    setSelectedTeamOptions(selectedTeamOptions);
  };

  const handleSelectStatus = (
    selectedStatusOptions: MultiValue<OptionType>,
  ) => {
    setValue(
      "status",
      selectedStatusOptions?.map((option) => option.value.toString()) || [],
    );
    setSelectedStatusOptions(selectedStatusOptions);
  };

  const handleClearFilter = () => {
    reset({
      startDate: "",
      endDate: "",
    });
    setSelectedSellerOptions([]);
    setParamsSeller({
      search: "",
    });
    setSelectedCustomerOptions([]);
    setParamsCustomer({
      search: "",
    });
    setSelectedTeamOptions([]);
    setParamsTeam({
      search: "",
    });
    setSelectedStatusOptions([]);
    setSelectedDate(undefined);
    setSelectedProductOptions([]);
    setValue("product", []);
  };

  const handleDateChange = (dateRange: DateRange | undefined) => {
    setSelectedDate(dateRange);
    if (dateRange?.from) {
      setValue("startDate", format(dateRange.from, "yyyy-MM-dd"));
    } else {
      setValue("startDate", "");
    }
    if (dateRange?.to) {
      setValue("endDate", format(dateRange.to, "yyyy-MM-dd"));
    } else {
      setValue("endDate", "");
    }
  };

  const handleFilterSubmit = handleSubmit(async (data) => {
    try {
      const parsedStartDate = data.startDate
        ? parse(data.startDate, "yyyy-MM-dd", new Date())
        : undefined;
      const parsedEndDate = data.endDate
        ? parse(data.endDate, "yyyy-MM-dd", new Date())
        : undefined;

      const startDate = data.startDate
        ? format(startOfDay(parsedStartDate!), "yyyy-MM-dd'T'HH:mm:ss.SSSxxx")
        : undefined;
      const endDate = data.endDate
        ? format(endOfDay(parsedEndDate!), "yyyy-MM-dd'T'HH:mm:ss.SSSxxx")
        : undefined;

      let sellerIds = "";
      let customerIds = "";
      let teamIds = "";
      let status = "";
      let product = "";

      if (data.sellerIds && data.sellerIds?.length > 0) {
        sellerIds = data.sellerIds.map((sellerId) => sellerId).join(",");
      }

      if (data.customerIds && data.customerIds?.length > 0) {
        customerIds = data.customerIds
          .map((customerId) => customerId)
          .join(",");
      }

      if (data.teamIds && data.teamIds?.length > 0) {
        teamIds = data.teamIds.map((teamIds) => teamIds).join(",");
      }

      if (data.status && data.status?.length > 0) {
        status = data.status.map((status) => status).join(",");
      }

      if (data.product && data.product?.length > 0) {
        product = data.product.map((product) => product).join(",");
      }

      const params = {
        startDate,
        endDate,
        sellerIds: isSeller ? user?._id : sellerIds || undefined,
        customerIds: customerIds || undefined,
        teamIds: teamIds || undefined,
        status: status || undefined,
        type: product || undefined,
      };

      setFilterParams(params);
    } catch (error) {
      console.log(error);
    }
  });

  return {
    handleFilterSubmit,
    handleClearFilter,
    register,
    errors,
    isSubmitting,
    selectedSellerOptions,
    selectedCustomerOptions,
    selectedTeamOptions,
    selectedStatusOptions,
    handleSelectSeller,
    handleSelectCustomer,
    handleSelectTeam,
    handleSelectStatus,
    isFetching,
    isLoading,
    handleLoadMore,
    isFetchingCustomer,
    isLoadingCustomer,
    handleLoadMoreCustomer,
    handleLoadMoreTeam,
    isFetchingTeam,
    isLoadingTeam,
    isSeller,
    setParamsCustomer,
    customerData,
    setParamsTeam,
    teamsData,
    setParamsSeller,
    sellerData,
    selectedDate,
    handleDateChange,
    selectedProductOptions,
    productOptions,
    handleSelectProduct,
    selected,
    changeMetric,
    handleFilterPopoverOpenChange,
  };
};
